import React from 'react';
import { MapPin, Phone, Mail, Clock, MessageCircle, Send } from 'lucide-react';
import { useHotel } from '../context/HotelContext';
import { useLanguage } from '../context/LanguageContext';
import ContactForm from './ContactForm';

const Contact = () => {
  const { hotelInfo } = useHotel();
  const { t } = useLanguage();

  const handleWhatsApp = () => {
    const msg = encodeURIComponent(t('contactSection.whatsappPrefill'));
    window.open(`https://wa.me/${hotelInfo.whatsapp}?text=${msg}`, '_blank');
  };

  const handleEmail = () => {
    window.location.href = `mailto:${hotelInfo.email}`;
  };

  return (
    <section id="contact" className="py-20 bg-white" data-testid="contact-section">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <p className="text-orange-500 font-semibold mb-2 text-sm uppercase tracking-wider">{t('contactSection.eyebrow')}</p>
          <h2 className="text-4xl lg:text-5xl font-bold mb-4 text-gray-900">{t('contactSection.heading')}</h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            {t('contactSection.lead')}
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Contact Info */}
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 shadow-lg border border-gray-100">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-orange-500" size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1 text-gray-900">{t('contactSection.addressLabel')}</h3>
                    <p className="text-gray-600">
                      {hotelInfo.address}<br />
                      {hotelInfo.city} {hotelInfo.postalCode}, {hotelInfo.country}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 shadow-lg border border-gray-100">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="text-orange-500" size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1 text-gray-900">{t('contactSection.phoneLabel')}</h3>
                    <a href={`tel:${hotelInfo.phone}`} className="text-gray-600 hover:text-orange-500 transition-colors">
                      {hotelInfo.phone}
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 shadow-lg border border-gray-100">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="text-orange-500" size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1 text-gray-900">{t('contactSection.emailLabel')}</h3>
                    <a href={`mailto:${hotelInfo.email}`} className="text-gray-600 hover:text-orange-500 transition-colors break-all">
                      {hotelInfo.email}
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 shadow-lg border border-gray-100">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="text-orange-500" size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1 text-gray-900">{t('contactSection.receptionLabel')}</h3>
                    <p className="text-gray-600">{t('contactSection.reception247')}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Contact Buttons */}
            <div className="flex flex-col justify-center space-y-4">
              <button
                onClick={handleWhatsApp}
                className="bg-gradient-to-r from-green-500 to-green-600 text-white p-6 rounded-xl font-bold text-lg hover:from-green-600 hover:to-green-700 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-3"
                data-testid="contact-whatsapp-button"
              >
                <MessageCircle size={28} />
                <span>{t('contactSection.whatsappBtn')}</span>
              </button>

              <button
                onClick={handleEmail}
                className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-6 rounded-xl font-bold text-lg hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-3"
                data-testid="contact-email-button"
              >
                <Send size={28} />
                <span>{t('contactSection.emailBtn')}</span>
              </button>

              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 text-center border-2 border-blue-200">
                <h3 className="font-bold text-xl mb-2 text-gray-900">{t('contactSection.bookDirectTitle')}</h3>
                <p className="text-gray-600 mb-4">{t('contactSection.bookDirectDesc')}</p>
                <button
                  onClick={() => document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' })}
                  className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-3 rounded-full font-semibold hover:from-orange-600 hover:to-orange-700 transition-all w-full"
                  data-testid="contact-check-availability-button"
                >
                  {t('contactSection.checkAvailability')}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-12 max-w-4xl mx-auto" id="contact-form">
          <ContactForm />
        </div>
      </div>
    </section>
  );
};

export default Contact;
